// Updated File: src/pages/Chat.jsx
// FIXED: Sender labels - "You:" for own messages (no name appended), "Support (AdminName):" for admin responses.
//        For admin view: "You:" for admin messages, "Customer (UserName):" for user messages.
//        Prevents appending own name to "You:" label.

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import io from 'socket.io-client';
import { chatAPI } from '../services/api';
import { 
  Send, 
  MessageCircle, 
  X, 
  Loader, 
  AlertCircle,
  Clock,
  CheckCircle,
  Lock
} from 'lucide-react';
import './css/Chat.css';

const Chat = () => {
  const [searchParams] = useSearchParams();
  const convoId = searchParams.get('convo');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [socket, setSocket] = useState(null);
  const [convoStatus, setConvoStatus] = useState('open');
  const [userTyping, setUserTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser.isAdmin || false;
  const userId = currentUser.id || null;

  useEffect(() => {
    if (!convoId) {
      setError('Aucun ID de conversation fourni');
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    const SOCKET_SERVER = "https://reyza.tn";

const newSocket = io(SOCKET_SERVER, {
  path: "/socket.io",
  auth: { token },
  transports: ["websocket"],   
  secure: true,
  withCredentials: true,
});

    newSocket.on('connect', () => {
      newSocket.emit('join-convo', convoId);
    });

    newSocket.on('new-message', (msg) => {
      setMessages(prev => [...prev, msg]);
      if (msg.senderType !== (isAdmin ? 'admin' : 'user')) {
        newSocket.emit('mark-read', { convoId });
      }
    });

    newSocket.on('message-persisted', ({ tempId, realId, message }) => {
      setMessages(prev => prev.map(m => m.id === tempId ? { ...m, id: realId } : m));
    });

    newSocket.on('user-typing', ({ isTyping }) => {
      setUserTyping(isTyping);
      if (isTyping) {
        setTimeout(() => setUserTyping(false), 2000);
      }
    });

    newSocket.on('messages-read', () => {
      setMessages(prev => prev.map(m => ({ ...m, is_read: 1 })));
    });

    newSocket.on('convo-closed', ({ message, reason }) => {
      setConvoStatus('closed');
      setError(message + (reason ? ` Raison : ${reason}` : ''));
    });

    newSocket.on('convo-reopened', ({ message }) => {
      setConvoStatus('open');
      setError('');
    });

    newSocket.on('error', (err) => {
      setError(err.message);
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [convoId, isAdmin]);

  useEffect(() => {
    if (!convoId || !socket) return;

    const fetchMessages = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await chatAPI.getConversation(convoId);
        setMessages(response.data.messages || []);
        setConvoStatus(response.data.conversation?.status || 'open');
        socket.emit('mark-read', { convoId });
      } catch (err) {
      setError('Impossible de charger la conversation : ' + (err.response?.data?.message || err.message));
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [convoId, socket]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleTyping = (e) => {
    if (convoStatus === 'closed') return;
    setInput(e.target.value);
    if (socket && e.target.value.length > 0) {
      socket.emit('typing', { convoId, isTyping: true });
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || convoStatus === 'closed') return;

    if (socket) {
      socket.emit('send-message', { convoId, content: input.trim() });
    }

    setInput('');
    inputRef.current.focus();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && convoStatus !== 'closed') {
      e.preventDefault();
      sendMessage();
    }
  };

  const isClosed = convoStatus === 'closed';

  if (!convoId) {
    return (
      <div className="chat-container">
        <div className="chat-error">
          <AlertCircle size={48} />
          <h2>Aucune conversation selectionnee</h2>
          <p>Demarrez une nouvelle discussion ou selectionnez-en une existante.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-container">
      <div className="chat-header">
        <MessageCircle size={24} />
        <h1>Chat assistance</h1>
        <span className="convo-id">#{convoId}</span>
        {error && <div className="chat-error-header"><AlertCircle size={16} /> {error}</div>}
        {isClosed && <span className="convo-status-closed"><Lock size={16} /> Fermee</span>}
      </div>

      <div className="chat-messages">
        {loading ? (
          <div className="chat-loading">
            <Loader size={32} className="spinner" />
            <p>Chargement des messages...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="chat-empty">
            <MessageCircle size={64} />
            <p>Aucun message pour l'instant. Commencez la conversation !</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isOwnMessage = msg.senderType === (isAdmin ? 'admin' : 'user');
            let senderLabel;
            if (isOwnMessage) {
              senderLabel = 'Vous :';
            } else {
              const otherLabel = isAdmin ? 'Client' : 'Assistance';
              const otherName = msg.senderName ? ` (${msg.senderName})` : '';
              senderLabel = `${otherLabel}${otherName}:`;
            }
            return (
              <div
                key={msg.id}
                className={`chat-message ${
                  isOwnMessage ? 'sent' : 'received'
                }`}
              >
                <div className="chat-message-content">
                  <div className="chat-sender-label">
                    {senderLabel}
                  </div>
                  <p>{msg.content}</p>
                  <span className="chat-message-time">
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    {msg.is_read ? <CheckCircle size={12} /> : <Clock size={12} />}
                  </span>
                </div>
              </div>
            );
          })
        )}
        {userTyping && !isClosed && (
          <div className="chat-typing">
            <div className="typing-indicator">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
            <span>Quelqu'un ecrit...</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-container">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={handleTyping}
          onKeyPress={handleKeyPress}
          placeholder={isClosed ? "Cette conversation est fermee" : "Tapez votre message..."}
          className="chat-input"
          disabled={loading || isClosed}
          readOnly={isClosed}
        />
        <button 
          onClick={sendMessage} 
          disabled={!input.trim() || isClosed || loading} 
          className="chat-send-btn"
        >
          <Send size={20} />
        </button>
      </div>
    </div>
  );
};

export default Chat;
